import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { UserModel3DLibraryItem } from "../../../features/floorplan3d/userModels/userModel3DTypes";
import {
  useToggleUserModelFavorite,
  useUserFavoriteUserModels,
} from "../../../gql/userModelFavorites";
import { favoriteHeartClassName } from "./ProductFavoriteButton";

type Props = {
  model: UserModel3DLibraryItem;
};

/**
 * Heart toggle for community / user 3D models — account-scoped (same device sync
 * as catalog favorites). Gray client-style heart.
 */
export const CommunityFavoriteButton: React.FC<Props> = ({ model }) => {
  const { t } = useTranslation();
  const { data, loading: favoritesLoading } = useUserFavoriteUserModels();
  const { toggle, loading: toggleLoading } = useToggleUserModelFavorite();
  const isFavorite = useMemo(
    () =>
      Boolean(
        data?.userFavoriteUserModels?.some((item) => item.id === model.id),
      ),
    [data?.userFavoriteUserModels, model.id],
  );

  if (favoritesLoading && !data?.userFavoriteUserModels) {
    return null;
  }
  if (!data?.userFavoriteUserModels) {
    return null;
  }

  const label = isFavorite
    ? t("favorites.removeFromFavorites")
    : t("favorites.addToFavorites");

  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={isFavorite}
      title={label}
      disabled={toggleLoading}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        void toggle(model, isFavorite);
      }}
      className={`${favoriteHeartClassName} ${toggleLoading ? "animate-pulse" : ""}`}
    >
      {isFavorite ? (
        <FaHeart size={14} aria-hidden />
      ) : (
        <FaRegHeart size={14} aria-hidden />
      )}
    </button>
  );
};
